/**
 * Utilitats compartides pels diàlegs d'enllaços de pagament (F5).
 */

import type { PaymentReviewReasonCode } from '@guilleumes/types';
import { toast } from 'sonner';
import { needsFreshPreview, paymentOrderErrorText } from '@/lib/payment-orders';

/**
 * Quan crear falla amb `TOTAL_CHANGED`: es deixa l'avís a la vista i es torna a demanar la proposta.
 * Qualsevol altre error només porta el toast del hook: la proposta que es veu segueix sent bona.
 */
export function handleCreateOrderError(
  error: unknown,
  actions: { setNotice: (text: string) => void; refetch: () => void },
): void {
  if (!needsFreshPreview(error)) return;
  actions.setNotice(paymentOrderErrorText(error));
  actions.refetch();
}

/** Per sobre de la pantalla completa del popover al mòbil (`z-[200]`). */
export const PAYMENT_DIALOG_Z = 'z-[210]';

/** Copia un text i ho diu amb un toast. Torna si ha anat bé. */
export async function copyPaymentLink(url: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(url);
    toast.success('Enllaç copiat');
    return true;
  } catch {
    toast.error("No s'ha pogut copiar l'enllaç");
    return false;
  }
}

/** Motius de «per revisar» que diuen «no se sap què ha passat amb el pagament a Stripe». */
const REREAD_REVIEW_REASONS: readonly PaymentReviewReasonCode[] = [
  'ORDER_HELD_UNRESOLVED',
  'CHECKOUT_COMPLETED_ON_CLOSED_ORDER',
  'CHECKOUT_DEACTIVATION_FAILED',
];
/** Estats en què encara pot faltar apuntar un cobrament (un de cobrat o retornat ja és al llibre). */
const REREAD_STATUSES: readonly string[] = ['OPEN', 'EXPIRED', 'CANCELLED', 'SUPERSEDED'];

/**
 * S'ofereix «Torna a llegir Stripe»? Només quan la sol·licitud és RETINGUDA (oberta amb la data
 * màxima passada: el sistema l'hauria caducada si no hi hagués un pagament en curs o una sessió
 * que no es deixa llegir), o és per revisar per «retinguda» (`ORDER_HELD_UNRESOLVED`) o per un
 * motiu de sessió. En un enllaç normal no surt.
 */
export function shouldOfferProviderReread(order: {
  status: string;
  pastDue: boolean;
  reviewReasons?: readonly string[] | null;
}): boolean {
  if (!REREAD_STATUSES.includes(order.status)) return false;
  if (order.status === 'OPEN' && order.pastDue) return true;
  return (order.reviewReasons ?? []).some((reason) =>
    (REREAD_REVIEW_REASONS as readonly string[]).includes(reason),
  );
}

/**
 * El que fa el botó «Torna a llegir Stripe» d'una fila: crida la relectura amb l'id d'AQUELLA
 * sol·licitud i res més. `undefined` si no hi ha sol·licitud (llavors no hi ha botó).
 */
export function rereadHandlerFor(
  order: { id: string } | null | undefined,
  reread: (orderId: string) => void,
): (() => void) | undefined {
  if (!order) return undefined;
  const orderId = order.id;
  return () => reread(orderId);
}
